
import { toast } from '@/components/ui/use-toast';
import { supabase } from '@/integrations/supabase/client';

/**
 * Centralized error handler that formats and displays errors consistently
 * @param error The error to handle
 * @param customMessage Optional custom message to display instead of the error message
 * @param showToastForAnonymous Whether to show toast for anonymous users (default: true)
 */
export async function handleError(error: unknown, customMessage?: string, showToastForAnonymous = true): Promise<void> {
  console.error('Error caught:', error);
  
  let errorMessage = 'Something went wrong. Please try again.';
  
  if (customMessage) {
    errorMessage = customMessage;
  } else if (error instanceof Error) {
    errorMessage = error.message;
  } else if (typeof error === 'string') {
    errorMessage = error;
  }
  
  // Handle captcha-related errors with a more specific message
  if (errorMessage.toLowerCase().includes('captcha') || 
      errorMessage.toLowerCase().includes('recaptcha')) {
    errorMessage = 'Captcha verification failed. Please try again.';
  }
  
  // Check if user is authenticated before showing toast
  const { data } = await supabase.auth.getSession();
  const isAuthenticated = !!data.session;
  
  // Only show toast if the error is likely meaningful to the user
  // Skip showing toast for network errors in non-critical operations
  // Or for unauthenticated users if showToastForAnonymous is false
  if ((!isNetworkError(error) || customMessage) && (isAuthenticated || showToastForAnonymous)) {
    toast({
      title: 'Error',
      description: errorMessage,
      variant: 'destructive',
    });
  }
}

/**
 * Handles API errors from Supabase or other services
 * @param error The error from the API
 * @param fallbackMessage A fallback message if no specific error info is available
 * @param showToast Whether to show a toast notification (default: true)
 * @param showToastForAnonymous Whether to show toast for anonymous users (default: true)
 */
export async function handleApiError(
  error: any, 
  fallbackMessage = 'API request failed', 
  showToast = true,
  showToastForAnonymous = true
): Promise<void> {
  let errorMessage = fallbackMessage;
  
  // Handle Supabase errors
  if (error?.message) {
    errorMessage = error.message;
  } else if (error?.error_description) {
    errorMessage = error.error_description;
  } else if (error?.details) {
    errorMessage = error.details;
  }
  
  console.error('API Error:', errorMessage, error);
  
  // Check if user is authenticated
  const { data } = await supabase.auth.getSession();
  const isAuthenticated = !!data.session;
  
  // Only show toast if requested - allows silent failures for non-critical operations
  // And only if authenticated or showToastForAnonymous is true
  if (showToast && (isAuthenticated || showToastForAnonymous)) {
    toast({
      title: 'Error',
      description: errorMessage,
      variant: 'destructive',
    });
  }
}

/**
 * Check if an error is likely a network-related error
 * @param error The error to check
 */
function isNetworkError(error: unknown): boolean {
  if (error instanceof Error) {
    const message = error.message.toLowerCase();
    return (
      message.includes('network') ||
      message.includes('connection') ||
      message.includes('offline') ||
      message.includes('failed to fetch') ||
      message.includes('cors')
    );
  }
  return false;
}

/**
 * Check if an error is related to captcha validation
 * @param error The error to check
 */
export function isCaptchaError(error: unknown): boolean {
  if (error instanceof Error) {
    const message = error.message.toLowerCase();
    return (
      message.includes('captcha') ||
      message.includes('recaptcha') ||
      message.includes('verification')
    );
  }
  return false;
}
